import React from "react";
import { Phone, Mail, Package, MapPin, CheckSquare, Square, Clock } from "lucide-react";
import { formatDate, formatSetupTime, formatDateTime } from "../../utils/datetime";
import MapNavButton from "../MapNavButton";

const STATUS_COLORS = {
  confirmed: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  cancelled: "bg-red-100 text-red-700",
  completed: "bg-blue-100 text-blue-700",
  rejected: "bg-red-100 text-red-700",
  refunded: "bg-gray-100 text-gray-700",
};

// Terminal booking states win over the payment gate — flipping a cancelled
// or completed booking back to "pending" would misrepresent the lifecycle.
const TERMINAL_STATUSES = new Set(["cancelled", "rejected", "completed", "refunded"]);

/**
 * The displayed booking status must agree with payment reality: if the
 * customer hasn't paid, the booking is NOT confirmed — regardless of what the
 * DB has stored (legacy data from before the iter181 gating fix can show
 * "confirmed" + "pending" together).
 */
export const deriveStatus = (b) => {
  const stored = (b.booking_status || b.status || "").toLowerCase();
  if (TERMINAL_STATUSES.has(stored)) return stored;
  const paid = (b.payment_status || "").toLowerCase() === "paid";
  if (!paid) return "pending";
  return stored || "confirmed";
};

export const BookingRow = ({ b, index, isSelected, onToggle }) => {
  const status = deriveStatus(b);

  return (
    <tr
      className={`border-b border-gray-50 hover:bg-[#FFFAF5] transition-colors ${isSelected ? "bg-[#E83E8C]/5" : ""}`}
      data-testid={`booking-row-${index}`}
    >
      <td className="p-3">
        <button onClick={onToggle} data-testid={`select-row-${index}`}>
          {isSelected ? <CheckSquare size={16} className="text-[#E83E8C]" /> : <Square size={16} className="text-gray-300" />}
        </button>
      </td>
      <td className="p-3 font-mono text-xs text-[#475569]">{b.id?.slice(0, 12) || "—"}</td>
      <td className="p-3 font-medium text-[#1E1B4B] whitespace-nowrap">{formatDate(b.date)}</td>
      <td className="p-3 text-xs text-[#1E1B4B] whitespace-nowrap min-w-[160px]">
        {/* Customer-selected setup time from the booking form. Legacy bookings
            without a stored slot fall back to any available time field so they
            don't render as blank. */}
        {(() => {
          const slot = b.setup_time || b.event_time || b.time_slot || "";
          const pretty = formatSetupTime(slot);
          return pretty ? (
            <span className="inline-flex items-center gap-1" title={slot}>
              <Clock size={10} className="text-[#E83E8C] shrink-0" /> {pretty}
            </span>
          ) : (
            <span className="text-[#94a3b8]">—</span>
          );
        })()}
      </td>
      <td className="p-3">
        <p className="font-medium text-[#1E1B4B] truncate max-w-[140px]">{b.customer_name || "—"}</p>
      </td>
      <td className="p-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-[#475569] flex items-center gap-1"><Phone size={10} /> {b.customer_phone || "—"}</span>
          {b.customer_email && <span className="text-xs text-[#94a3b8] flex items-center gap-1 truncate max-w-[150px]"><Mail size={10} /> {b.customer_email}</span>}
        </div>
      </td>
      <td className="p-3">
        <span className="px-2 py-0.5 bg-[#E83E8C]/10 text-[#E83E8C] rounded-full text-xs font-medium flex items-center gap-1 w-fit">
          <Package size={10} /> {b.package || "—"}
        </span>
      </td>
      <td className="p-3 text-xs text-[#475569] max-w-[100px] truncate">{b.occasion || "—"}</td>
      <td className="p-3 text-xs text-[#475569] max-w-[180px]">
        <div className="flex items-center gap-1.5">
          <span className="flex items-start gap-1 truncate flex-1" title={b.address}><MapPin size={10} className="shrink-0 mt-0.5" /> {b.address || "—"}</span>
          <MapNavButton lat={b.location_lat} lng={b.location_lng} address={b.address} compact testId={`booking-navigate-${index}`} />
        </div>
      </td>
      <td className="p-3 font-semibold text-[#1E1B4B] whitespace-nowrap">
        {/* payment_amount & final_amount are stored in paise — divide by 100 for display. */}
        ₹{(((b.payment_amount || b.final_amount || 0)) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </td>
      <td className="p-3">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${b.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
          {b.payment_status || "pending"}
        </span>
      </td>
      <td className="p-3">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[status] || "bg-gray-100 text-gray-700"}`}>
          {status}
        </span>
      </td>
      <td className="p-3 text-xs text-[#475569] truncate max-w-[100px]">{b.sales_executive || "—"}</td>
      <td className="p-3 text-xs text-[#94a3b8] whitespace-nowrap">{formatDateTime(b.created_at)}</td>
    </tr>
  );
};
